import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FocusEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import styled from "styled-components";

import { Field } from "../Field";
import { TextInput } from "../Input";
import { PopoverSurface } from "../foundations/popover";
import { usePopoverDismiss } from "../foundations/popoverDismiss";

export const defaultColorPickerColor = 0x3366cc;

export const defaultColorPickerPalette = [
  0x3366cc, 0x2563eb, 0x0891b2, 0x0f766e, 0x16a34a, 0x65a30d, 0xca8a04, 0xd97706, 0xdc2626,
  0xe11d48, 0xc026d3, 0x7c3aed, 0x475569, 0x64748b, 0x1f2937, 0x111827,
] as const;

export type ColorPickerProps = {
  id?: string;
  value: number;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  palette?: readonly number[];
  ariaLabel?: string;
  dialogLabel?: string;
  allowCustomColors?: boolean;
  customColorLabel?: string;
  onChange: (value: number) => void;
};

const maxRgbColor = 0xffffff;
const hexColorPattern = /^#?[0-9a-fA-F]{6}$/;

const Root = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const Trigger = styled.button`
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 2.25rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid ${({ theme }) => theme.color.bg.surfaceOverlay};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: transparent;
  color: ${({ theme }) => theme.color.text.default};
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background-color 120ms ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.border.disabled};
    background: ${({ theme }) => theme.color.bg.surfaceSunken};
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
`;

const Swatch = styled.span`
  width: 1.1rem;
  height: 1.1rem;
  flex: 0 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 0.25rem;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
`;

const Value = styled.span`
  min-width: 0;
  overflow: hidden;
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Popover = styled(PopoverSurface)`
  top: calc(100% + 0.25rem);
  left: 0;
  display: grid;
  width: min(18rem, calc(100vw - 2rem));
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surface};
  box-shadow: ${({ theme }) => theme.elevation.shadow.overlay};
`;

const Palette = styled.div`
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 0.5rem;

  @media (max-width: 560px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const ColorButton = styled.button`
  aspect-ratio: 1;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: 0.35rem;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 2px;
  }

  /* 눌린 스와치의 선택 외곽은 border.selected 로 남는다 — focus 와 값이 갈렸으므로
     이제 이미 눌린 스와치에 포커스가 와도 두 외곽이 서로 구별된다. */
  &[aria-pressed="true"] {
    outline: 2px solid ${({ theme }) => theme.color.border.selected};
    outline-offset: 2px;
  }
`;

const SelectedColor = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  padding: 0.15rem 0.1rem;
`;

const SelectedSwatch = styled.span`
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
`;

const HexField = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const HexInput = styled(TextInput)<{ $editable: boolean }>`
  cursor: ${({ $editable }) => ($editable ? "text" : "default")};

  input {
    cursor: ${({ $editable }) => ($editable ? "text" : "default")};
  }
`;

const CustomPalette = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 7rem;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 0.55rem;
  background:
    linear-gradient(to top, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0)),
    linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0)),
    linear-gradient(90deg, #dc2626, #ca8a04, #16a34a, #0891b2, #2563eb, #7c3aed, #dc2626);
  cursor: crosshair;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 2px;
  }
`;

const CustomPaletteMarker = styled.span<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: 0.75rem;
  height: 0.75rem;
  border: 2px solid white;
  border-radius: 999px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.55),
    0 1px 4px rgba(0, 0, 0, 0.35);
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

export function ColorPicker({
  id,
  value,
  disabled = false,
  className,
  style,
  palette = defaultColorPickerPalette,
  ariaLabel,
  dialogLabel = "Select color",
  allowCustomColors = false,
  customColorLabel = "Pick custom color",
  onChange,
}: ColorPickerProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const dismissRefs = useMemo(() => [rootRef], []);
  const hexValue = formatColorPickerColorHex(value);
  const [hexInput, setHexInput] = useState(hexValue);
  const customPalettePosition = getCustomPalettePosition(value);
  const swatchStyle = useMemo<CSSProperties>(() => ({ backgroundColor: hexValue }), [hexValue]);
  const canEditHexCode = allowCustomColors;

  useEffect(() => {
    setHexInput(hexValue);
  }, [hexValue]);

  usePopoverDismiss({
    open,
    refs: dismissRefs,
    onClose: () => setOpen(false),
  });

  const commitColor = useCallback(
    (nextColor: number) => {
      if (!isColorPickerColorValue(nextColor)) {
        return;
      }
      onChange(nextColor);
    },
    [onChange],
  );

  const handleCustomPaletteClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      commitColor(getColorPickerValueFromPoint(event.currentTarget, event.clientX, event.clientY));
    },
    [commitColor],
  );

  const handleHexInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!canEditHexCode) {
        event.currentTarget.value = hexInput;
        return;
      }

      const nextValue = event.target.value;
      setHexInput(nextValue);
      const parsed = parseColorPickerHex(nextValue);
      if (parsed !== null) {
        commitColor(parsed);
      }
    },
    [canEditHexCode, commitColor, hexInput],
  );

  const handleHexInputBlur = useCallback(() => {
    setHexInput(hexValue);
  }, [hexValue]);

  const handleHexInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (!canEditHexCode) {
        event.currentTarget.blur();
      }
    },
    [canEditHexCode],
  );

  const handleHexInputPointerDown = useCallback(
    (event: ReactMouseEvent<HTMLInputElement>) => {
      if (!canEditHexCode) {
        event.preventDefault();
      }
    },
    [canEditHexCode],
  );

  return (
    <Root ref={rootRef} className={className} style={style}>
      <Trigger
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel ?? "Color " + hexValue}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Swatch style={swatchStyle} aria-hidden="true" />
        <Value>{hexValue}</Value>
      </Trigger>
      {open && !disabled ? (
        <Popover role="dialog" aria-label={dialogLabel}>
          <SelectedColor aria-label={"Selected color " + hexValue}>
            <SelectedSwatch style={swatchStyle} aria-hidden="true" />
            <HexField>
              <Field label="Hex" layout="inline" labelWidth="2rem">
                <HexInput
                  $editable={canEditHexCode}
                  value={hexInput}
                  inputSize="sm"
                  inputMode="text"
                  maxLength={7}
                  pattern="#?[0-9A-Fa-f]{6}"
                  aria-readonly={canEditHexCode ? undefined : true}
                  tabIndex={canEditHexCode ? undefined : -1}
                  sanitize={sanitizeColorPickerHexInput}
                  onChange={handleHexInputChange}
                  onBlur={handleHexInputBlur}
                  onFocus={handleHexInputFocus}
                  onPointerDown={handleHexInputPointerDown}
                />
              </Field>
            </HexField>
          </SelectedColor>
          <Palette>
            {palette.map((color) => {
              const optionHex = formatColorPickerColorHex(color);
              const selected =
                normalizeColorPickerColor(value) === normalizeColorPickerColor(color);
              return (
                <ColorButton
                  key={optionHex}
                  type="button"
                  style={{ backgroundColor: optionHex }}
                  aria-label={"Use color " + optionHex}
                  aria-pressed={selected}
                  onClick={() => commitColor(color)}
                />
              );
            })}
          </Palette>
          {allowCustomColors ? (
            <CustomPalette
              type="button"
              aria-label={customColorLabel}
              onClick={handleCustomPaletteClick}
            >
              <CustomPaletteMarker $x={customPalettePosition.x} $y={customPalettePosition.y} />
            </CustomPalette>
          ) : null}
        </Popover>
      ) : null}
    </Root>
  );
}

export function formatColorPickerColorHex(value: number): string {
  return "#" + normalizeColorPickerColor(value).toString(16).padStart(6, "0").toUpperCase();
}

function sanitizeColorPickerHexInput(value: string): string {
  const normalized = value.trimStart().toUpperCase();
  const hasHash = normalized.startsWith("#");
  const hex = normalized
    .replace(/^#/, "")
    .replace(/[^0-9A-F]/g, "")
    .slice(0, 6);
  return hasHash ? "#" + hex : hex;
}

function parseColorPickerHex(value: string): number | null {
  const trimmed = value.trim();
  if (!hexColorPattern.test(trimmed)) {
    return null;
  }
  return Number.parseInt(trimmed.replace(/^#/, ""), 16);
}

function getColorPickerValueFromPoint(
  element: HTMLElement,
  clientX: number,
  clientY: number,
): number {
  const rect = element.getBoundingClientRect();
  const x = clamp01((clientX - rect.left) / Math.max(rect.width, 1));
  const y = clamp01((clientY - rect.top) / Math.max(rect.height, 1));
  const { red, green, blue } = hsvToRgb(x * 360, 0.88, 1 - y * 0.82);
  return (red << 16) + (green << 8) + blue;
}

function getCustomPalettePosition(value: number): { x: number; y: number } {
  const { red, green, blue } = parseColorPickerColor(formatColorPickerColorHex(value));
  const { hue, value: brightness } = rgbToHsv(red, green, blue);
  return {
    x: (hue / 360) * 100,
    y: ((1 - brightness) / 0.82) * 100,
  };
}

function parseColorPickerColor(hex: string): { red: number; green: number; blue: number } {
  const normalized = hex.replace(/^#/, "");
  return {
    red: Number.parseInt(normalized.slice(0, 2), 16),
    green: Number.parseInt(normalized.slice(2, 4), 16),
    blue: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function hsvToRgb(
  hue: number,
  saturation: number,
  value: number,
): { red: number; green: number; blue: number } {
  const chroma = value * saturation;
  const segment = (hue % 360) / 60;
  const second = chroma * (1 - Math.abs((segment % 2) - 1));
  const match = value - chroma;
  const [red, green, blue] = getHueSegmentRgb(segment, chroma, second);
  return {
    red: Math.round((red + match) * 255),
    green: Math.round((green + match) * 255),
    blue: Math.round((blue + match) * 255),
  };
}

function getHueSegmentRgb(
  segment: number,
  chroma: number,
  second: number,
): [number, number, number] {
  if (segment < 1) return [chroma, second, 0];
  if (segment < 2) return [second, chroma, 0];
  if (segment < 3) return [0, chroma, second];
  if (segment < 4) return [0, second, chroma];
  if (segment < 5) return [second, 0, chroma];
  return [chroma, 0, second];
}

function rgbToHsv(red: number, green: number, blue: number): { hue: number; value: number } {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  if (delta === 0) {
    return { hue: 0, value: max };
  }
  if (max === r) {
    return { hue: ((g - b) / delta + (g < b ? 6 : 0)) * 60, value: max };
  }
  if (max === g) {
    return { hue: ((b - r) / delta + 2) * 60, value: max };
  }
  return { hue: ((r - g) / delta + 4) * 60, value: max };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function isColorPickerColorValue(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= maxRgbColor;
}

function normalizeColorPickerColor(value: number): number {
  if (!Number.isFinite(value)) {
    return defaultColorPickerColor;
  }
  return Math.max(0, Math.min(maxRgbColor, Math.trunc(value)));
}

ColorPicker.displayName = "ColorPicker";
